import { SignInOptions } from '@aws-amplify/auth/dist/esm/providers/cognito/types';
import {
  AuthSession,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser,
  GetCurrentUserOutput,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup
} from 'aws-amplify/auth';

import { Authenticator, LoginResult, User, UserNameType } from './authenticator';
import { Amplify } from 'aws-amplify';
import { DEFAULT_PASSWORD_POLICY, PasswordPolicy } from './password-policy';

export enum COGNITO_SIGN_IN_STEP {
  CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
  DONE = 'DONE',
  CONFIRM_SIGN_IN_WITH_TOTP_CODE = 'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
  CONFIRM_SIGN_IN_WITH_EMAIL_CODE = 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE',
  CONTINUE_SIGN_IN_WITH_TOTP_SETUP = 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP'
}

export enum COGNITO_MFA_TYPE {
  TOTP = 'totp',
  SMS = 'sms',
  EMAIL = 'email'
}

const options: SignInOptions = {
  clientMetadata: {
    client: 'Infuse'
  }
};

export class CognitoAuthenticator extends Authenticator {
  static readonly MFA_METHOD = {
    DISABLED: 'DISABLED',
    EMAIL: 'EMAIL',
    TOTP: 'TOTP'
  };

  async login(username: string, password: string): Promise<LoginResult> {
    const { nextStep } = await signIn({
      username,
      password,
      options
    });

    if (
      nextStep.signInStep &&
      nextStep.signInStep === COGNITO_SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED
    ) {
      return {
        isLoggedIn: false,
        setNewPassword: async (password: string) => {
          await confirmSignIn({
            challengeResponse: password,
            options
          });
          return { isLoggedIn: true };
        }
      };
    }

    // if challenge is undefined check the preferred method; if NOMFA we can assume it is not set in cognito
    if (nextStep.signInStep === COGNITO_SIGN_IN_STEP.DONE) {
      return { isLoggedIn: true };
    }

    // User has MFA totp enabled
    if (nextStep.signInStep === COGNITO_SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_TOTP_CODE) {
      return {
        isLoggedIn: false,
        setMFACode: async (otp: string) => {
          await confirmSignIn({
            challengeResponse: otp,
            options
          });
          return { isLoggedIn: true };
        }
      };
    }

    if (nextStep.signInStep === COGNITO_SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_EMAIL_CODE) {
      return {
        isLoggedIn: false,
        setMailMFACode: async (otp: string) => {
          await confirmSignIn({
            challengeResponse: otp,
            options
          });
          return { isLoggedIn: true };
        }
      };
    }

    // User must setup MFA
    // For url info, see: https://github.com/google/google-authenticator/wiki/Key-Uri-Format
    if (nextStep.signInStep === COGNITO_SIGN_IN_STEP.CONTINUE_SIGN_IN_WITH_TOTP_SETUP) {
      const { sharedSecret } = await setUpTOTP();
      return {
        isLoggedIn: false,
        totpSetupDetails: {
          authenticatorCode: sharedSecret,
          qrCodeUrl: `otpauth://totp/Infuse:${username}?secret=${sharedSecret}&issuer=Infuse`
        },
        setupMFA: async (otp: string) => {
          await verifyTOTPSetup({
            code: otp,
            options
          });
          await updateMFAPreference({
            totp: 'PREFERRED'
          });
          return { isLoggedIn: true };
        }
      };
    }

    return { isLoggedIn: true };
  }

  enableForgotPassword(): boolean {
    return true;
  }

  async forgotPassword(username: string) {
    return await resetPassword({
      username,
      options
    });
  }

  async forgotPasswordConfirm(username: string, password: string, code: string) {
    return confirmResetPassword({
      username,
      newPassword: password,
      confirmationCode: code,
      options
    });
  }

  async getAuthenticationHeader(): Promise<string> {
    const session: AuthSession = await fetchAuthSession();
    if (session.tokens?.accessToken) {
      return `Bearer ${session.tokens?.accessToken.toString()}`;
    }
    const { authorizationHeader } = await this.getUser();
    return authorizationHeader;
  }

  async getUser(): Promise<User> {
    const cognitoUser: GetCurrentUserOutput = await getCurrentUser();
    const session: AuthSession = await fetchAuthSession();
    const attributes: FetchUserAttributesOutput = await fetchUserAttributes();

    return {
      email: attributes.email ?? cognitoUser.username,
      authorizationHeader: `Bearer ${session.tokens?.accessToken.toString() ?? ''}`
    };
  }

  userNameType(): UserNameType {
    return UserNameType.EMAIL;
  }

  requiresCredentials(): boolean {
    return true;
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      return !!session.tokens?.accessToken;
    } catch (err) {
      return false;
    }
  }

  logout(): Promise<void> {
    return signOut();
  }

  getPasswordPolicy(): PasswordPolicy {
    const passwordFormat = Amplify.getConfig().Auth?.Cognito?.passwordFormat;
    if (!passwordFormat) {
      return DEFAULT_PASSWORD_POLICY;
    }

    return {
      minimumLength: passwordFormat.minLength,
      requireLowercase: passwordFormat.requireLowercase,
      requireSymbols: passwordFormat.requireSpecialCharacters,
      requireNumbers: passwordFormat.requireNumbers,
      requireUppercase: passwordFormat.requireUppercase
    };
  }
}
